/**
 * PurchaseRegisterConsultationController — page `/registre-des-achats.html`
 * (EPIC-018 / 18.3 — Consultation du registre des achats §8.bis.E).
 *
 *  - filtres `fiscalYear` (par défaut année courante) OU `from/to`
 *    (mutuellement exclusifs, bascule via le select « Filtrer par ») ;
 *  - tableau dynamique chargé via `GET /purchases/register/entries` ;
 *  - encart « Clôture des exercices » alimenté par
 *    `GET /purchases/register/closures` ;
 *  - bouton « Exporter (provisoire) » → `POST /purchases/register/exports/provisional`
 *    déclenche un téléchargement éphémère (form POST submit) ;
 *  - boutons « Clôturer l'exercice N-1 » → ouvrent la modale clôture
 *    annuelle (18.6) : chargement du récap via
 *    `GET /purchases/register/years/{year}/pre-close-preview` puis
 *    confirmation via `POST /purchases/register/closures`. Les boutons
 *    sont automatiquement libellés et masqués selon l'état de la clôture
 *    (`_updateCloseYearButtons`).
 *  - bouton « Télécharger le PDF » dans l'encart closures → ouvre l'URL
 *    `/purchases/register/closures/{fiscalYear}/pdf` ;
 *  - gate `bic_vente` (D3) : si l'API retourne 403 `not_bic_vente_eligible`,
 *    affiche un toast et désactive les filtres.
 *
 * Convention `frontend/CLAUDE.md` : aucune construction HTML inline en
 * JS — uniquement clone de `<template>` + remplissage `data-slot`.
 */

import BaseController from "./base_controller.js";
import { api, APIError, API_BASE_URL } from "/js/services/api.js";

const PAYMENT_METHOD_LABELS = {
  virement: "Virement",
  prelevement: "Prélèvement",
  carte: "Carte",
  especes: "Espèces",
  cheque: "Chèque",
  autre: "Autre",
};

export default class PurchaseRegisterConsultationController extends BaseController {
  static targets = [
    "modeSelect",
    "yearSelect",
    "fromInput",
    "toInput",
    "entriesSummary",
    "entriesTbody",
    "entriesTfoot",
    "closuresTbody",
    "unclosedYearCard", "unclosedYearLabel",
    "closeYearButton",
    "closureModal",
    "closureTitleYear",
    "closureBodyYear",
    "closureWarnYear",
    "closureLoading",
    "closureSummary",
    "closureEntriesCount",
    "closureTotalIncl",
    "closureVatBreakdown",
    "closureSealedAmount",
    "closureManualAmount",
    "closureCorrectionsAmount",
    "closureError",
    "closureErrorMessage",
    "closureConfirmButton",
  ];

  connect() {
    this._fetchSeq = 0;
    this._restoreFromQueryString();
    this._syncFilterVisibility();
    this.refresh();
    this._fetchClosures();

    this._onEntryCreated = () => this.refresh();
    window.addEventListener("purchase-register:entry-created", this._onEntryCreated);
  }

  disconnect() {
    if (this._onEntryCreated) {
      window.removeEventListener("purchase-register:entry-created", this._onEntryCreated);
    }
  }

  // ---------------------------------------------------------------------------
  // Filters
  // ---------------------------------------------------------------------------

  changeMode() {
    this._syncFilterVisibility();
    this.refresh();
  }

  refresh() {
    this._syncQueryString();
    this._fetchEntries();
  }

  _syncFilterVisibility() {
    if (!this.hasModeSelectTarget) return;
    const mode = this.modeSelectTarget.value;
    const isYearMode = mode === "annee";
    if (this.hasYearSelectTarget && this.yearSelectTarget.parentElement) {
      this.yearSelectTarget.parentElement.hidden = !isYearMode;
    }
    if (this.hasFromInputTarget && this.fromInputTarget.parentElement) {
      this.fromInputTarget.parentElement.hidden = isYearMode;
    }
    if (this.hasToInputTarget && this.toInputTarget.parentElement) {
      this.toInputTarget.parentElement.hidden = isYearMode;
    }
  }

  _restoreFromQueryString() {
    const params = new URLSearchParams(window.location.search);
    if (params.has("fiscalYear") && this.hasYearSelectTarget) {
      this.yearSelectTarget.value = params.get("fiscalYear");
      if (this.hasModeSelectTarget) this.modeSelectTarget.value = "annee";
    }
    if (params.has("from") && this.hasFromInputTarget) {
      this.fromInputTarget.value = params.get("from");
      if (this.hasModeSelectTarget) this.modeSelectTarget.value = "custom";
    }
    if (params.has("to") && this.hasToInputTarget) {
      this.toInputTarget.value = params.get("to");
      if (this.hasModeSelectTarget) this.modeSelectTarget.value = "custom";
    }
  }

  _syncQueryString() {
    const params = new URLSearchParams();
    const filters = this._currentFilters();
    if (filters.fiscalYear) params.set("fiscalYear", String(filters.fiscalYear));
    if (filters.from) params.set("from", filters.from);
    if (filters.to) params.set("to", filters.to);
    const qs = params.toString();
    window.history.replaceState(
      null,
      "",
      window.location.pathname + (qs ? "?" + qs : ""),
    );
  }

  _currentFilters() {
    const filters = {};
    const mode = this.hasModeSelectTarget ? this.modeSelectTarget.value : "annee";
    if (mode === "annee") {
      if (this.hasYearSelectTarget && this.yearSelectTarget.value) {
        filters.fiscalYear = parseInt(this.yearSelectTarget.value, 10) || new Date().getFullYear();
      } else {
        filters.fiscalYear = new Date().getFullYear();
      }
    } else {
      if (this.hasFromInputTarget && this.fromInputTarget.value) filters.from = this.fromInputTarget.value;
      if (this.hasToInputTarget && this.toInputTarget.value) filters.to = this.toInputTarget.value;
    }
    return filters;
  }

  // ---------------------------------------------------------------------------
  // Fetch + render
  // ---------------------------------------------------------------------------

  async _fetchEntries() {
    const seq = ++this._fetchSeq;
    const filters = this._currentFilters();
    try {
      const response = await api.purchaseRegister.listEntries(filters);
      if (seq !== this._fetchSeq) return;
      const entries = Array.isArray(response?.data) ? response.data : [];
      const totals = response?.totals ?? null;
      const period = response?.period ?? null;
      this._renderRows(entries);
      this._renderTotals(entries, totals, period);
      this._renderSummary(entries.length, period);
    } catch (error) {
      if (seq !== this._fetchSeq) return;
      if (error instanceof APIError && error.status === 403) {
        this.showError("Le registre des achats nécessite une activité BIC vente active.");
        this._renderRows([]);
        return;
      }
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de charger le registre des achats.";
      this.showError(msg);
      this._renderRows([]);
    }
  }

  async _fetchClosures() {
    try {
      const response = await api.purchaseRegister.listClosures();
      const closures = Array.isArray(response?.data) ? response.data : [];
      this._renderClosures(closures);
      this._renderUnclosedYear(closures);
    } catch (error) {
      if (error instanceof APIError && error.status === 403) return;
      this._renderClosures([]);
    }
  }

  _renderRows(entries) {
    if (!this.hasEntriesTbodyTarget) return;
    this._clearChildren(this.entriesTbodyTarget);

    if (entries.length === 0) {
      this.entriesTbodyTarget.appendChild(this.cloneTemplate("tpl-purchase-register-empty"));
      return;
    }

    for (const entry of entries) {
      const fragment = this.cloneTemplate("tpl-purchase-register-entry-row");
      const isCorrection = entry.correctionOfEntryId !== null && entry.correctionOfEntryId !== undefined;
      const exclVat = Number(entry.amountExclVat ?? 0) || 0;
      const vat = Number(entry.vatAmount ?? 0) || 0;
      const incl = Number(entry.amountInclVat ?? 0) || 0;

      this.fillSlots(fragment, {
        entryNumber: entry.entryNumber ? "#" + String(entry.entryNumber).padStart(3, "0") : "—",
        entryDateLabel: this._formatDate(entry.entryDate),
        supplierName: entry.supplierName ?? "—",
        description: entry.description ?? "—",
        pieceReference: entry.pieceReference ?? "—",
        paymentMethodLabel: PAYMENT_METHOD_LABELS[entry.paymentMethod] ?? entry.paymentMethod ?? "—",
        amountExclVat: this._formatCurrency(exclVat, isCorrection ? -1 : 1),
        vatAmount: vat === 0 ? "—" : this._formatCurrency(vat, isCorrection ? -1 : 1),
        amountInclVat: this._formatCurrency(incl, isCorrection ? -1 : 1),
      });

      const timeEl = fragment.querySelector('[data-slot-attr-datetime="entryDateIso"]');
      if (timeEl && entry.entryDate) timeEl.setAttribute("datetime", entry.entryDate);

      if (isCorrection) {
        const rowEl = fragment.querySelector('[data-purchase-register-consultation-target="entryRow"]');
        if (rowEl) {
          rowEl.style.background = "color-mix(in oklab, var(--warning-text) 7%, transparent)";
          rowEl.querySelectorAll(".is-amount").forEach((cell) => cell.classList.add("text-error"));
        }
      }

      const actionCell = fragment.querySelector('[data-slot-element="actionCell"]');
      if (actionCell) {
        if (isCorrection) {
          actionCell.appendChild(this.cloneTemplate("tpl-purchase-register-correction-action"));
        } else {
          const correctFrag = this.cloneTemplate("tpl-purchase-register-correct-link");
          const link = correctFrag.querySelector("a");
          if (link && entry.id) {
            const ns = "purchase-register-manual-entry";
            link.setAttribute(`data-${ns}-entry-id-param`, entry.id);
            link.setAttribute(`data-${ns}-entry-number-param`, String(entry.entryNumber ?? ""));
            link.setAttribute(`data-${ns}-supplier-name-param`, entry.supplierName ?? "");
            link.setAttribute(`data-${ns}-amount-excl-vat-param`, entry.amountExclVat ?? "0");
            link.setAttribute(`data-${ns}-vat-amount-param`, entry.vatAmount ?? "0");
            link.setAttribute(`data-${ns}-amount-incl-vat-param`, entry.amountInclVat ?? "0");
            link.setAttribute(`data-${ns}-payment-method-param`, entry.paymentMethod ?? "");
            link.setAttribute(`data-${ns}-piece-reference-param`, entry.pieceReference ?? "");
            link.setAttribute(`data-${ns}-entry-date-param`, entry.entryDate ?? "");
          }
          actionCell.appendChild(correctFrag);
        }
      }

      this.entriesTbodyTarget.appendChild(fragment);
    }
  }

  _renderTotals(entries, totals, period) {
    if (!this.hasEntriesTfootTarget) return;
    this._clearChildren(this.entriesTfootTarget);

    if (!totals || entries.length === 0) {
      return;
    }

    const periodLabel = period?.fiscalYear
      ? `Totaux période ${period.fiscalYear} :`
      : period?.from && period?.to
        ? `Totaux période ${this._formatDate(period.from)} – ${this._formatDate(period.to)} :`
        : "Totaux période :";

    const totalsRow = this.cloneTemplate("tpl-purchase-register-totals");
    this.fillSlots(totalsRow, {
      periodLabel,
      totalExclVat: this._formatCurrency(Number(totals.amountExclVat) || 0),
      totalVat: this._formatCurrency(Number(totals.vatAmount) || 0),
      totalInclVat: this._formatCurrency(Number(totals.amountInclVat) || 0),
    });
    this.entriesTfootTarget.appendChild(totalsRow);

    const vatByRate = Array.isArray(totals.vatByRate) ? totals.vatByRate : [];
    for (const bucket of vatByRate) {
      const rateValue = Number(bucket.rate) || 0;
      const rateLabel = rateValue > 0
        ? `— dont TVA ${this._formatRate(bucket.rate)} :`
        : "— dont sans TVA (espèces / fournisseur en franchise) :";
      const fragment = this.cloneTemplate("tpl-purchase-register-vat-rate-row");
      this.fillSlots(fragment, {
        rateLabel,
        amountExclVat: this._formatCurrency(Number(bucket.amountExclVat) || 0),
        vatAmount: rateValue === 0 ? "—" : this._formatCurrency(Number(bucket.vatAmount) || 0),
        amountInclVat: this._formatCurrency(Number(bucket.amountInclVat) || 0),
      });
      this.entriesTfootTarget.appendChild(fragment);
    }
  }

  _renderSummary(count, period) {
    if (!this.hasEntriesSummaryTarget) return;
    if (count === 0) {
      this.entriesSummaryTarget.textContent = "Aucune écriture sur la période sélectionnée.";
      return;
    }
    const periodLabel = period?.fiscalYear
      ? ` (exercice ${period.fiscalYear})`
      : "";
    const plural = count > 1 ? "s" : "";
    this.entriesSummaryTarget.textContent = `${count} écriture${plural} · scellée${plural}${periodLabel}`;
  }

  _renderClosures(closures) {
    if (!this.hasClosuresTbodyTarget) return;
    this._clearChildren(this.closuresTbodyTarget);

    if (closures.length === 0) {
      this.closuresTbodyTarget.appendChild(this.cloneTemplate("tpl-purchase-register-closures-empty"));
      return;
    }

    for (const closure of closures) {
      const fragment = this.cloneTemplate("tpl-purchase-register-closure-row");
      this.fillSlots(fragment, {
        fiscalYear: String(closure.fiscalYear ?? ""),
        closedAtLabel: this._formatDateTime(closure.closedAt),
        entriesCount: String(closure.entriesCount ?? 0),
        entriesSealedHashShort: closure.entriesSealedHashShort ?? "",
        closedByLabel: "—",
      });

      const timeEl = fragment.querySelector('[data-slot-attr-datetime="closedAtIso"]');
      if (timeEl && closure.closedAt) timeEl.setAttribute("datetime", closure.closedAt);

      const downloadLink = fragment.querySelector('[data-slot-attr-data-fiscal-year="fiscalYear"]');
      if (downloadLink && closure.fiscalYear) {
        downloadLink.setAttribute("data-fiscal-year", String(closure.fiscalYear));
      }

      this.closuresTbodyTarget.appendChild(fragment);
    }
  }

  _renderUnclosedYear(closures) {
    const today = new Date();
    const previousYear = today.getFullYear() - 1;
    const previousIsClosed = closures.some((c) => Number(c.fiscalYear) === previousYear);

    if (this.hasUnclosedYearCardTarget) {
      this.unclosedYearCardTarget.hidden = previousIsClosed;
    }
    if (this.hasUnclosedYearLabelTarget) {
      this.unclosedYearLabelTarget.textContent = String(previousYear);
    }

    this._updateCloseYearButtons(previousYear, previousIsClosed);
  }

  _updateCloseYearButtons(previousYear, previousIsClosed) {
    const buttons = this.element.querySelectorAll('[data-close-year]');
    buttons.forEach((btn) => {
      btn.setAttribute("data-close-year", String(previousYear));
      btn.textContent = `Clôturer l'exercice ${previousYear}`;
      btn.disabled = previousIsClosed;
      if (previousIsClosed) {
        btn.setAttribute("title", `L'exercice ${previousYear} est déjà clôturé.`);
      } else {
        btn.removeAttribute("title");
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  exportProvisional(event) {
    if (event && typeof event.preventDefault === "function") event.preventDefault();
    const filters = this._currentFilters();
    this._exportProvisionalViaFetch(filters);
  }

  async _exportProvisionalViaFetch(filters) {
    try {
      const body = {};
      if (filters.fiscalYear) body.fiscalYear = filters.fiscalYear;
      if (filters.from) body.from = filters.from;
      if (filters.to) body.to = filters.to;
      const response = await fetch(`${API_BASE_URL}/purchases/register/exports/provisional`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        this.showError("Export indisponible.");
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const disposition = response.headers.get("Content-Disposition") ?? "";
      const match = disposition.match(/filename="([^"]+)"/);
      link.download = match ? match[1] : "registre-achats.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      this.showError("Export indisponible.");
    }
  }

  // ---------------------------------------------------------------------------
  // Modale clôture annuelle (18.6 — §8.bis.F.2)
  // ---------------------------------------------------------------------------

  openCloseYearModal(event) {
    if (event && typeof event.preventDefault === "function") event.preventDefault();
    if (!(event?.currentTarget instanceof HTMLElement)) return;
    const yearRaw = event.currentTarget.getAttribute("data-close-year");
    const year = Number(yearRaw);
    if (!Number.isInteger(year) || year < 2000) return;

    this._pendingClosureYear = year;
    this._setClosureYearLabel(year);
    this._toggleClosureModal(true);
    this._resetClosureFeedback();
    this._fetchClosurePreview(year);
  }

  closeCloseYearModal(event) {
    if (event && typeof event.preventDefault === "function") event.preventDefault();
    this._toggleClosureModal(false);
  }

  async confirmCloseYear(event) {
    if (event && typeof event.preventDefault === "function") event.preventDefault();
    if (!this.hasClosureConfirmButtonTarget) return;
    if (this.closureConfirmButtonTarget.disabled) return;
    if (!Number.isInteger(this._pendingClosureYear)) return;

    this.closureConfirmButtonTarget.disabled = true;
    this._hideClosureError();

    try {
      await api.purchaseRegister.closeYear(this._pendingClosureYear);
      this._toggleClosureModal(false);
      this._fetchClosures();
      this.refresh();
    } catch (error) {
      this.closureConfirmButtonTarget.disabled = false;
      let message = "La clôture a échoué.";
      if (error instanceof APIError) {
        if (error.status === 409) {
          message = `L'exercice ${this._pendingClosureYear} est déjà clôturé.`;
        } else if (error.status === 422) {
          message = error.message || "La clôture n'est disponible qu'à partir du 1er janvier de l'année suivante.";
        } else if (error.status === 403) {
          message = "Le registre des achats nécessite une activité BIC vente active.";
        } else if (error.status === 503) {
          message = "Service de clôture indisponible. Réessayez plus tard.";
        } else if (error.message) {
          message = error.message;
        }
      }
      this._showClosureError(message);
    }
  }

  async _fetchClosurePreview(year) {
    try {
      const preview = await api.purchaseRegister.getPreClosePreview(year);
      this._renderClosurePreview(preview);
    } catch (error) {
      this._showClosureError(
        error instanceof APIError && error.message
          ? error.message
          : "Impossible de charger le récapitulatif de l'exercice.",
      );
      if (this.hasClosureLoadingTarget) this.closureLoadingTarget.hidden = true;
    }
  }

  _renderClosurePreview(preview) {
    if (this.hasClosureLoadingTarget) this.closureLoadingTarget.hidden = true;
    if (this.hasClosureSummaryTarget) this.closureSummaryTarget.hidden = false;

    if (this.hasClosureEntriesCountTarget) {
      this.closureEntriesCountTarget.textContent = String(preview?.entriesCount ?? 0);
    }
    if (this.hasClosureTotalInclTarget) {
      this.closureTotalInclTarget.textContent = this._formatCurrency(
        Number(preview?.totalAll?.amountInclVat ?? 0),
      );
    }
    if (this.hasClosureSealedAmountTarget) {
      this.closureSealedAmountTarget.textContent =
        this._formatCurrency(Number(preview?.sealedFromInvoicesAmount ?? 0))
        + ` · ${preview?.sealedFromInvoicesCount ?? 0}`;
    }
    if (this.hasClosureManualAmountTarget) {
      this.closureManualAmountTarget.textContent =
        this._formatCurrency(Number(preview?.manualEntriesAmount ?? 0))
        + ` · ${preview?.manualEntriesCount ?? 0}`;
    }
    if (this.hasClosureCorrectionsAmountTarget) {
      this.closureCorrectionsAmountTarget.textContent =
        this._formatCurrency(Number(preview?.correctionsAmount ?? 0))
        + ` · ${preview?.correctionsCount ?? 0}`;
    }

    this._renderClosureVatBreakdown(Array.isArray(preview?.totalsByVatRate) ? preview.totalsByVatRate : []);

    if (preview?.alreadyClosed) {
      this._showClosureError(`L'exercice ${preview.fiscalYear} est déjà clôturé.`);
      if (this.hasClosureConfirmButtonTarget) this.closureConfirmButtonTarget.disabled = true;
    } else if (preview?.yearTerminated === false) {
      this._showClosureError("La clôture n'est disponible qu'à partir du 1er janvier de l'année suivante.");
      if (this.hasClosureConfirmButtonTarget) this.closureConfirmButtonTarget.disabled = true;
    } else if (this.hasClosureConfirmButtonTarget) {
      this.closureConfirmButtonTarget.disabled = false;
    }
  }

  _renderClosureVatBreakdown(buckets) {
    if (!this.hasClosureVatBreakdownTarget) return;
    this._clearChildren(this.closureVatBreakdownTarget);
    for (const bucket of buckets) {
      const rateValue = Number(bucket?.rate) || 0;
      const rateLabel = rateValue > 0
        ? `— dont TVA ${this._formatRate(bucket.rate)}`
        : "— dont sans TVA";
      const fragment = this.cloneTemplate("tpl-purchase-register-closure-vat-row");
      this.fillSlots(fragment, {
        rateLabel,
        vatAmount: this._formatCurrency(Number(bucket?.vatAmount ?? 0)),
      });
      this.closureVatBreakdownTarget.appendChild(fragment);
    }
  }

  _toggleClosureModal(open) {
    if (!this.hasClosureModalTarget) return;
    this.closureModalTarget.hidden = !open;
    if (open) {
      this.closureModalTarget.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  _setClosureYearLabel(year) {
    const yearStr = String(year);
    if (this.hasClosureTitleYearTarget) this.closureTitleYearTarget.textContent = yearStr;
    if (this.hasClosureBodyYearTarget) this.closureBodyYearTarget.textContent = yearStr;
    if (this.hasClosureWarnYearTarget) this.closureWarnYearTarget.textContent = yearStr;
  }

  _resetClosureFeedback() {
    if (this.hasClosureLoadingTarget) this.closureLoadingTarget.hidden = false;
    if (this.hasClosureSummaryTarget) this.closureSummaryTarget.hidden = true;
    if (this.hasClosureConfirmButtonTarget) this.closureConfirmButtonTarget.disabled = false;
    this._hideClosureError();
  }

  _showClosureError(message) {
    if (!this.hasClosureErrorTarget || !this.hasClosureErrorMessageTarget) return;
    this.closureErrorMessageTarget.textContent = message;
    this.closureErrorTarget.hidden = false;
  }

  _hideClosureError() {
    if (this.hasClosureErrorTarget) this.closureErrorTarget.hidden = true;
  }

  downloadClosurePdf(event) {
    if (event && typeof event.preventDefault === "function") event.preventDefault();
    const link = event.currentTarget;
    if (!(link instanceof HTMLElement)) return;
    const fiscalYear = link.getAttribute("data-fiscal-year");
    if (!fiscalYear) return;
    window.location.href = api.purchaseRegister.closurePdfUrl(fiscalYear);
  }

  // openCorrection : handler relocalisé sur le controller frère
  // `purchase-register-manual-entry` (EPIC-018 / 18.5) — le lien
  // "Corriger" cloné depuis `tpl-purchase-register-correct-link` cible
  // directement `purchase-register-manual-entry#openCorrection` avec les
  // data-params nécessaires (entryId, entryNumber, supplierName, montants,
  // paymentMethod, pieceReference, entryDate).
  refreshAfterEntryCreated() {
    this.refresh();
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _clearChildren(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  _formatCurrency(value, sign = 1) {
    const num = (Number(value) || 0) * sign;
    return num.toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " €";
  }

  _formatRate(raw) {
    const num = Number(raw);
    if (!Number.isFinite(num)) return String(raw);
    const rounded = Math.round(num);
    return Math.abs(num - rounded) < 0.005
      ? `${rounded} %`
      : `${num.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`;
  }

  _formatDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  }

  _formatDateTime(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
}
